import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import i18n from "@/plugins/i18n";
import store from "@/store";

declare module "vue-router" {
  interface RouteMeta {
    auth?: boolean;
    titleKey?: string;
  }
}

const meta = {
  auth: true,
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: () => import("@/views/SimManagement/SimListView.vue"),
    meta: { auth: true, titleKey: "Page_SIM" },
  },
  {
    path: "/otas",
    name: "otas",
    component: () => import("@/views/OtaManagement/OtaView.vue"),
    meta: { auth: true, titleKey: "ota.Page_OTA" },
    children: [
      {
        path: "/otas",
        name: "ota_type_init",
        component: () => import("@/views/OtaManagement/OtaListView.vue"),
        meta: { auth: true, titleKey: "ota.Page_OTA" },
      },
      {
        path: "/otas/:packageAID",
        name: "ota_type",
        component: () => import("@/views/OtaManagement/OtaListView.vue"),
        meta: { auth: true, titleKey: "ota.Page_OTA" },
      },
      {
        path: "package/detail/:packageAID/:otaVersion/:label",
        name: "ota_item",
        component: () => import("@/views/OtaManagement/OtaItemView.vue"),
        meta: { auth: true, titleKey: "ota.Page_OTA" },
      },
    ],
  },
  {
    path: "/sims",
    name: "sim",
    component: () => import("@/views/SimManagement/SimView.vue"),
    meta: { auth: true, titleKey: "Page_SIM" },
    children: [
      {
        path: ":id/:cid",
        name: "sim_item",
        component: () => import("@/views/SimManagement/SimItemView.vue"),
        meta: { auth: true, titleKey: "Page_SIM" },
      },
    ],
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/SettingsView.vue"),
    meta: { auth: true, titleKey: "Setting" },
    children: [
      {
        path: "theme",
        name: "settingTheme",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "mail",
        name: "settingMail",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "password",
        name: "settingPassword",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "generate",
        name: "settingGenerate",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "setting-iot-safe",
        name: "settingIotSafe",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "setting-eim",
        name: "settingEim",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "notification",
        name: "settingNotification",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "member",
        name: "settingMember",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
      {
        path: "sim",
        name: "settingSim",
        component: () => import("@/views/SettingsView.vue"),
        meta: { auth: true, titleKey: "Setting" },
      },
    ],
  },
  {
    path: "/logs",
    name: "logs",
    component: () => import("@/views/LogsView.vue"),
    meta: { auth: true, titleKey: "Log" },
    children: [
      {
        path: "system-log",
        name: "systemLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "raw-api-log",
        name: "rawApiLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "raw-proxy-log",
        name: "rawProxyLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "raw-ota-api-log",
        name: "rawOtaApiLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "raw-ota-service-log",
        name: "rawOtaServiceLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "audit-log",
        name: "auditLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "openvpn-log",
        name: "openVpnLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "archived-log",
        name: "archivedLog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
      {
        path: "bulk-ota-log",
        name: "bulkOTALog",
        component: () => import("@/views/LogsView.vue"),
        meta: { auth: true, titleKey: "Log" },
      },
    ],
  },
  {
    path: "/change-password/:token",
    name: "change_password",
    component: () => import("@/views/Authentication/ChangePasswordView.vue"),
    meta: { titleKey: "changePassword_title" },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Authentication/LoginView.vue"),
    meta: { titleKey: "signIn_title" },
  },
  {
    path: "/verify_mfa",
    name: "verify_mfa",
    component: () => import("@/views/Authentication/VerifyMfaView.vue"),
    meta: { titleKey: "Mfa_title" },
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("@/views/Authentication/LogoutView.vue"),
    meta: { auth: true, titleKey: "logout" },
  },
  {
    path: "/forgot-password",
    name: "forgot_password",
    component: () => import("@/views/Authentication/ForgotPasswordView.vue"),
    meta: { titleKey: "forgotPassword_title" },
  },
  {
    path: "/forgot-password-confirm",
    name: "forgot_password_confirm",
    component: () => import("@/views/Authentication/ForgotPasswordConfirmView.vue"),
    meta: { titleKey: "forgotPassword_title" },
  },
  {
    path: "/members",
    redirect: "/settings/member",
  },
  {
    path: "/cmps",
    name: "cmp_list",
    component: () => import("@/views/CmpManagement/CmpListView.vue"),
    meta: meta,
  },
  {
    path: "/cmps/:id/sims",
    name: "cmp_sim_status",
    component: () => import("@/views/CmpManagement/CmpSimStatusView.vue"),
    meta: { auth: true, titleKey: "Page_cmp" },
  },
  {
    path: "/cmps/:id/sims/:iccid",
    name: "cmp_sim_detail",
    component: () => import("@/views/CmpManagement/CmpSimDetailView.vue"),
    meta: { auth: true, titleKey: "Page_cmp" },
  },
  {
    path: "/personal/setting",
    name: "personal_setting",
    component: () => import("@/views/Authentication/MfaView.vue"),
    meta: { auth: true, titleKey: "PersonalSettings" },
  },
  {
    path: "/iot-safe",
    name: "iot_safe",
    component: () => import("@/views/IotSafeManagement/IotSafeListView.vue"),
    meta: { auth: true, titleKey: "IotSafe" },
  },
  {
    path: "/iot-safe/:simId",
    name: "iot_safe_item",
    component: () => import("@/views/IotSafeManagement/IotSafeItemView.vue"),
    meta: { auth: true, titleKey: "IotSafe" },
  },
  {
    path: "/iot-safe/:simId/certificates/:certId",
    name: "iot_safe_certificate_item",
    component: () => import("@/views/IotSafeManagement/ClientCertItemView.vue"),
    meta: { auth: true, titleKey: "IotSafe" },
  },
  {
    path: "/iot-safe/:simId/files/:fileId",
    name: "iot_safe_file_item",
    component: () => import("@/views/IotSafeManagement/IotSafeFileItemView.vue"),
    meta: { auth: true, titleKey: "IotSafe" },
  },
  {
    path: "/esim/list",
    name: "esim_list",
    component: () => import("@/views/ESimManagement/ESimListView.vue"),
    meta: { auth: true, titleKey: "eSIM" },
  },
  {
    path: "/esim/:eid",
    name: "esim_detail",
    component: () => import("@/views/ESimManagement/ESimItemView.vue"),
    meta: { auth: true, titleKey: "eSIM" },
  },
  { path: "/:pathMatch(.*)*", name: "not_found", component: () => import("@/views/NotFoundView.vue"), meta: { titleKey: "PageNotFound" } },
  { path: "/openapi-docs", name: "openapi_doc", component: () => import("@/views/APISwagger/OpenAPISwaggerView.vue"), meta: { titleKey: "OpenApiDocs" } },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// アプリケーション名を取得する関数
const getAppName = (): string => {
  const settings = store.getters.getUserSettings;
  if (settings && Array.isArray(settings)) {
    const appNameSetting = settings.find((s: any) => s.key === "system_config_app_name");
    if (appNameSetting && appNameSetting.value && appNameSetting.value !== "null") {
      return appNameSetting.value;
    }
  }
  return "Applet Console";
};

// ページタイトルを更新する関数
const updatePageTitle = (titleKey?: string): void => {
  const appName = getAppName();
  const { t } = i18n.global;

  if (titleKey) {
    const pageTitle = t(titleKey);
    document.title = `${pageTitle} | ${appName}`;
  } else {
    document.title = appName;
  }
};

// ナビゲーションガード: ページ遷移時にタイトルを更新
router.afterEach((to) => {
  const titleKey = to.meta.titleKey;
  updatePageTitle(titleKey);
});

router.beforeEach((to) => {
  if (process.env.VUE_APP_ENABLE_CMP !== "1" && ["cmp_list", "cmp_sim_status", "cmp_sim_detail"].includes(String(to.name ?? ""))) {
    return { name: "home" };
  }
});

export default router;
export { updatePageTitle };
